import React, { useState } from 'react';
import styled from 'styled-components';
import { pdf } from '@react-pdf/renderer';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { slideInFromRightWeighted } from '../../shared/component/Animation';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { BlackFitBtn } from '../../shared/component/headless/button/BtnBody';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { getBtnName } from '../../shared/constants/btnNames';
import { useMemberInfo } from '../../shared/hooks/useApi/useMemberInfo';
import { useCareer } from '../../shared/hooks/useApi/useCareer';
import { useAboutPageSettingState } from '../../shared/hooks/useApi/useAboutPageSetting';
import { useCanShowAboutPageSetting } from '../../shared/hooks/useAboutPageSettingVisibility';
import DocumentHorizontalCV from './DocumentHorizontalCV';
import DocumentVerticalCV from './DocumentVerticalCV';
import { AboutContentMode, PdfLayout } from '../../shared/enum/EnumRepository';
import { paperTextureSmall } from '../../shared/component/paperTexture';

const AboutCvPdfModal: React.FC = () => {
  const { isClosing, clearModal } = useModalStore();
  const { memberInfo } = useMemberInfo();
  const { careerList } = useCareer();
  const { aboutPageSetting } = useAboutPageSettingState();
  const canShowAboutPageSetting = useCanShowAboutPageSetting();
  const [contentMode, setContentMode] = useState<AboutContentMode>(AboutContentMode.Orig);
  const [pdfLayout, setPdfLayout] = useState<PdfLayout>(PdfLayout.Vertical);

  if (!memberInfo) return null;

  const handleGenerate = async () => {
    const docProps = {
      memberInfo,
      careerList,
      careerSectionOrder: canShowAboutPageSetting
        ? aboutPageSetting?.careerSectionOrder
        : undefined,
      contentMode,
    };
    const doc =
      pdfLayout === PdfLayout.Horizontal ? (
        <DocumentHorizontalCV {...docProps} />
      ) : (
        <DocumentVerticalCV {...docProps} />
      );
    const blob = await pdf(doc).toBlob();

    const cvFileStem =
      contentMode === AboutContentMode.ENG
        ? (memberInfo.nameEn ?? memberInfo.name)
        : memberInfo.name;

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download =
      pdfLayout === PdfLayout.Horizontal
        ? `CV_${cvFileStem}_landscape.pdf`
        : `CV_${cvFileStem}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
    clearModal(400);
  };

  return (
    <LeftModalFrame onClick={(e) => e.stopPropagation()} $isClosing={isClosing}>
      <Comp>
        <Title>{getModalMessage('ABOUT_PDF_OPTIONS_TITLE')}</Title>

        <OptionSection>
          <OptionLabel>{getModalMessage('EXPORT_LANGUAGE_LABEL')}</OptionLabel>
          <RadioGroup>
            <CheckboxComp>
              <Checkbox
                type="radio"
                name="aboutCvVersion"
                checked={contentMode === AboutContentMode.Orig}
                onChange={() => setContentMode(AboutContentMode.Orig)}
              />
              {getModalMessage('EXPORT_LANGUAGE_ORIGINAL')}
            </CheckboxComp>
            <CheckboxComp>
              <Checkbox
                type="radio"
                name="aboutCvVersion"
                checked={contentMode === AboutContentMode.ENG}
                onChange={() => setContentMode(AboutContentMode.ENG)}
              />
              {getModalMessage('EXPORT_LANGUAGE_ENGLISH')}
            </CheckboxComp>
          </RadioGroup>
        </OptionSection>

        <OptionSection>
          <OptionLabel>{getModalMessage('ABOUT_PDF_LAYOUT_LABEL')}</OptionLabel>
          <RadioGroup>
            <CheckboxComp>
              <Checkbox
                type="radio"
                name="aboutCvPdfLayout"
                checked={pdfLayout === PdfLayout.Vertical}
                onChange={() => setPdfLayout(PdfLayout.Vertical)}
              />
              {getModalMessage('ABOUT_PDF_LAYOUT_PORTRAIT')}
            </CheckboxComp>
            <CheckboxComp>
              <Checkbox
                type="radio"
                name="aboutCvPdfLayout"
                checked={pdfLayout === PdfLayout.Horizontal}
                onChange={() => setPdfLayout(PdfLayout.Horizontal)}
              />
              {getModalMessage('ABOUT_PDF_LAYOUT_LANDSCAPE')}
            </CheckboxComp>
          </RadioGroup>
        </OptionSection>

        <ButtonContainer>
          <HeadlessBtn
            value={getBtnName('GENERATE')}
            handleClick={handleGenerate}
            StyledBtn={BlackFitBtn}
          />
        </ButtonContainer>
      </Comp>
    </LeftModalFrame>
  );
};

const LeftModalFrame = styled.div<{ $isClosing: boolean }>`
  position: absolute;
  right: clamp(20px, 2vw, 30px);
  bottom: clamp(70px, 6vw, 100px);
  width: 12vw;
  min-width: 180px;
  height: clamp(25vh, fit-content, 50vh);

  padding: 20px;

  opacity: ${({ $isClosing }) => ($isClosing ? 0 : 1)};
  transform: ${({ $isClosing }) =>
    $isClosing ? 'translateX(100%)' : 'translateX(0)'};

  transition: all 0.6s ease-out;
  ${paperTextureSmall}
  ${slideInFromRightWeighted}
`;

const Comp = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: clamp(10px, 5vh, 30px);
`;

const Title = styled.div`
  margin-bottom: 1.5vh;
  ${({ theme }) => theme.typography.Body_02_1};
`;

const OptionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OptionLabel = styled.div`
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_02};
  margin-bottom: 1vh;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CheckboxComp = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const Checkbox = styled.input.attrs({ type: 'radio' })`
  appearance: none;
  width: 12px;
  height: 12px;
  border: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
  margin-right: 10px;
  outline: none;
  transition: border 0.2s, background 0.2s;
  position: relative;
  cursor: pointer;
  &:hover {
    border-color: ${({ theme }) => theme.colors.color_Gray_03};
  }

  &:checked {
    border-color: ${({ theme }) => theme.colors.color_Gray_03};
  }

  &:checked::after {
    content: '';
    position: absolute;
    left: 4px;
    top: -3px;
    width: 5px;
    height: 10px;
    border: solid ${({ theme }) => theme.colors.color_Gray_02};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export default AboutCvPdfModal;
